import React, { useState, useEffect, useMemo, useCallback } from 'react'
import Pager from './Pagers'
import Prev from './Prev'
import Next from './Next'
import Total from './Total'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useConfig } from '../ConfigProvider'

const Pagination = props => {
  const { className, currentPage, displayTotal, onChange, pageSize, total } = props

  const [paginationTotal, setPaginationTotal] = useState(total)
  const [internalPageSize, setInternalPageSize] = useState(pageSize)
  const [isMobile, setIsMobile] = useState(false)

  const { themeColor, primaryColorLevel } = useConfig()

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mediaQuery = window.matchMedia('(max-width: 575.98px)')
    const handleResize = event => setIsMobile(event.matches)

    setIsMobile(mediaQuery.matches)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleResize)
    } else {
      mediaQuery.addListener(handleResize)
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleResize)
      } else {
        mediaQuery.removeListener(handleResize)
      }
    }
  }, [])

  const getInternalPageCount = useMemo(() => {
    if (typeof paginationTotal === 'number') {
      return Math.ceil(paginationTotal / internalPageSize)
    }
    return 1
  }, [paginationTotal, internalPageSize])

  const getValidCurrentPage = useCallback(count => {
    let value = parseInt(count, 10)
    const pageCount = getInternalPageCount
    if (isNaN(value) || value < 1) value = 1
    if (pageCount && value > pageCount) value = pageCount
    return value
  }, [getInternalPageCount])

  const [internalCurrentPage, setInternalCurrentPage] = useState(currentPage ? getValidCurrentPage(currentPage) : 1)

  useEffect(() => {
    if (total !== paginationTotal) setPaginationTotal(total)
    if (pageSize !== internalPageSize) setInternalPageSize(pageSize)
    if (currentPage !== internalCurrentPage) setInternalCurrentPage(getValidCurrentPage(currentPage))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, pageSize, currentPage])

  const onPaginationChange = val => {
    const validPage = getValidCurrentPage(val)
    setInternalCurrentPage(validPage)
    onChange?.(validPage)
  }

  const onPrev = useCallback(() => {
    const newPage = internalCurrentPage - 1
    const validPage = getValidCurrentPage(newPage)
    setInternalCurrentPage(validPage)
    onChange?.(validPage)
  }, [onChange, internalCurrentPage, getValidCurrentPage])

  const onNext = useCallback(() => {
    const newPage = internalCurrentPage + 1
    const validPage = getValidCurrentPage(newPage)
    setInternalCurrentPage(validPage)
    onChange?.(validPage)
  }, [onChange, internalCurrentPage, getValidCurrentPage])

  // Dark mode only: no light mode classes
  const pagerClass = {
    // px-2 + min-w give each page number room, so 3 digit pages don't touch
    default: 'pagination-pager min-w-[2rem] px-2 text-center',
    inactive: 'pagination-pager-inactive',
    active: `dark:bg-${themeColor}-${primaryColorLevel} bg-green-600 text-white dark:text-gray-100 hover:dark:bg-${themeColor}-${primaryColorLevel}`,
    disabled: 'pagination-pager-disabled'
  }

  // gap-1 adds spacing between prev, pages, and next
  const paginationClass = classNames('pagination flex items-center gap-1', className)

  return (
    <div className={paginationClass}>
      {displayTotal && !isMobile && <Total total={total} />}
      <Prev
        currentPage={internalCurrentPage}
        pagerClass={pagerClass}
        onPrev={onPrev}
      />
      <Pager
        onChange={onPaginationChange}
        pageCount={getInternalPageCount}
        currentPage={internalCurrentPage}
        pagerClass={pagerClass}
        isMobile={isMobile}
      />
      <Next
        currentPage={internalCurrentPage}
        pageCount={getInternalPageCount}
        pagerClass={pagerClass}
        onNext={onNext}
      />
    </div>
  )
}

Pagination.propTypes = {
  total: PropTypes.number,
  displayTotal: PropTypes.bool,
  pageSize: PropTypes.number,
  className: PropTypes.string,
  currentPage: PropTypes.number,
  onChange: PropTypes.func,
}

Pagination.defaultProps = {
  pageSize: 1,
  currentPage: 1,
  total: 5,
  displayTotal: false,
}

export default Pagination